import React from "react";
import { BRAND } from "@/lib/brand";
import { Counter } from "@/components/Counter";
import { useContent } from "@/lib/cms";
import { API } from "@/lib/api";
import { ShieldCheck, BadgeCheck, MessageSquare, Send, Users, Trophy, HeartHandshake, Zap, Bot, Award, Workflow, UserPlus, Plug, Megaphone, Cloud } from "lucide-react";

// Resolve logo URLs — if relative (starts with /storage or /uploads), prefix with backend domain.
const resolveLogoUrl = (url) => {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/storage")) return API + url;
  if (url.startsWith("/uploads")) return API + "/storage" + url;
  return url;
};

// Fallback client list (used if CMS returns empty). Kept in-sync with CMS testimonials.client_logos seed.
const FALLBACK_CLIENTS = [
  { name: "Haldiram's", logo_url: "/brand/clients/haldirams.png" },
  { name: "Fortis Hospitals", logo_url: "/brand/clients/fortishospital.png" },
  { name: "SBI Card", logo_url: "/brand/clients/sbicard.png" },
  { name: "Ather Energy", logo_url: "/brand/clients/Atherenergy.png" },
  { name: "GSI Cargo", logo_url: "/brand/clients/gsicargo.png" },
  { name: "River Indie", logo_url: "/brand/clients/riverindie.png" },
  { name: "Nutrikalp", logo_url: "/brand/clients/nutrikalp.png" },
  { name: "FinWorks360", logo_url: "/brand/clients/FinWorks360.png" },
  { name: "Haldwani Naturals", logo_url: "/brand/clients/haldwaninaturals.png" },
  { name: "Apppl.com", logo_url: "/brand/clients/Apppl.png" },
  { name: "GSA Buildwell", logo_url: "/brand/clients/gsabuildwell.png" },
  { name: "Follow Foots Vacation Pvt. Ltd.", logo_url: "/brand/clients/followfoots.png" },
];

// Highly-recognizable brands — logo only, no company-name label next to it.
const HIDE_NAME_FOR = new Set(["Haldiram's", "Fortis Hospitals", "SBI Card", "Ather Energy"]);

const clientColors = ["#2563eb", "#0891b2", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#14b8a6", "#db2777", "#6366f1", "#0ea5e9", "#84cc16", "#f97316"];

// Genuine trust signals only — capabilities + outcomes we can stand behind.
const trustBadges = [
  { Icon: ShieldCheck, label: "Official WhatsApp Business API", tone: "emerald" },
  { Icon: Cloud, label: "Official Meta WhatsApp Cloud API", tone: "blue" },
  { Icon: Bot, label: "AI Chatbot", tone: "emerald" },
  { Icon: BadgeCheck, label: "Green Tick Verification", tone: "emerald" },
  { Icon: Users, label: "Multi-Agent Team Inbox", tone: "blue" },
  { Icon: Plug, label: "CRM Integration", tone: "blue" },
  { Icon: Workflow, label: "API Integration", tone: "emerald" },
  { Icon: Megaphone, label: "Bulk Broadcasting", tone: "blue" },
  { Icon: Zap, label: "24–48 Hour Setup", tone: "emerald" },
  { Icon: HeartHandshake, label: "Dedicated Onboarding Support", tone: "blue" },
];

const toneMap = {
  emerald: "bg-emerald-50 border-emerald-100 text-emerald-800 [&_svg]:text-emerald-600",
  blue: "bg-blue-50 border-blue-100 text-blue-800 [&_svg]:text-blue-600",
};

const stats = [
  { value: 900, suffix: "+", label: "Businesses Served" },
  { value: 7, suffix: "+ Cr", label: "WhatsApp messages delivered" },
  { value: 122, suffix: "+", label: "Active businesses" },
  { value: 13, suffix: "+ Yrs", label: "Industry experience" },
];

export const TrustBar = () => {
  const { data: testimonialData } = useContent("testimonials", {});
  const cmsClients = testimonialData?.client_logos || [];
  const clients = cmsClients.length ? cmsClients : FALLBACK_CLIENTS;
  return (
    <section data-testid="trust-bar" className="py-16 sm:py-20 pb-20 sm:pb-24 bg-slate-50/60 border-y border-slate-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-600">Trusted across India</p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {BRAND.trust.clients} businesses · {BRAND.trust.messages} messages delivered
          </h2>
          <p className="mt-3 text-slate-600 text-sm sm:text-base">
            From travel agencies to D2C brands, schools to clinics — teams across {BRAND.trust.activeBusinesses} active industries run their customer conversations on Wappit.
          </p>
        </div>

        {/* Genuine capability badges */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-2.5" data-testid="trust-badges-row">
          {trustBadges.map(({ Icon, label, tone }) => (
            <span
              key={label}
              data-testid={`trust-badge-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className={`inline-flex items-center gap-2 rounded-2xl border px-3.5 py-2 text-sm font-semibold shadow-premium-sm hover:shadow-premium hover:-translate-y-0.5 transition-all ${toneMap[tone]}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </span>
          ))}
        </div>

        {/* Animated stat strip */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="trust-stats">
          {stats.map((s) => (
            <div key={s.label} className="rounded-2xl bg-white border border-slate-200 px-5 py-5 text-center sm:text-left hover:shadow-premium hover:-translate-y-0.5 transition-all">
              <div className="text-3xl sm:text-4xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="text-xs sm:text-sm text-slate-600 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Client logos — CMS-driven infinite marquee. Vibrant by default, extra pop on hover,
            focus-dim other cards when one is hovered, subtle emerald border transition, edge fades. */}
        <div className="mt-14">
          <p className="text-center text-[11px] uppercase tracking-[0.25em] font-bold text-slate-500">Trusted by Growing Businesses Across India</p>
          <div
            className="mt-8 relative overflow-hidden group/marquee"
            data-testid="client-logos"
            style={{
              WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
              maskImage:        "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
            }}
          >
            <div
              className="flex gap-6 sm:gap-8 w-max animate-[wappit-marquee_55s_linear_infinite] group-hover/marquee:[animation-play-state:paused]"
              aria-hidden={false}
            >
              {[...clients, ...clients].map((c, i) => {
                const initials = c.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
                const color = clientColors[i % clientColors.length];
                const hideName = HIDE_NAME_FOR.has(c.name);
                // Track image load failure so we can gracefully fall back to initials + name.
                const inner = c.logo_url ? (
                  <img
                    src={resolveLogoUrl(c.logo_url)}
                    alt={c.name}
                    loading="lazy"
                    onError={(e) => {
                      const el = e.currentTarget;
                      el.style.display = "none";
                      const fb = el.parentElement?.querySelector("[data-fallback]");
                      if (fb) fb.style.display = "inline-flex";
                    }}
                    className="max-h-16 max-w-[200px] object-contain px-1 opacity-90 group-hover:opacity-100 transition-opacity duration-[250ms]"
                  />
                ) : null;
                const fallback = (
                  <span
                    data-fallback
                    style={{ display: c.logo_url ? "none" : "inline-flex" }}
                    className="items-center gap-2.5"
                  >
                    <span
                      className="h-12 w-12 rounded-xl flex items-center justify-center text-white text-[14px] font-extrabold tracking-tight flex-shrink-0"
                      style={{ background: color, fontFamily: "Outfit, sans-serif" }}
                      aria-hidden="true"
                    >
                      {initials}
                    </span>
                    {!hideName && (
                      <span className="text-sm font-semibold tracking-tight text-slate-700 whitespace-nowrap">{c.name}</span>
                    )}
                  </span>
                );
                const CardTag = c.website ? "a" : "div";
                const cardProps = c.website
                  ? { href: c.website, target: "_blank", rel: "noopener noreferrer" }
                  : {};
                return (
                  <CardTag
                    key={c.name + "-" + i}
                    {...cardProps}
                    data-testid={i < clients.length ? `client-logo-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}` : undefined}
                    aria-hidden={i >= clients.length}
                    /* Card: subtle depth, emerald focus on hover, dim siblings when marquee is hovered */
                    className="h-24 min-w-[220px] px-6 rounded-2xl bg-white border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.06)] flex items-center justify-center gap-3 flex-shrink-0 opacity-90 transition-all duration-[250ms] group hover:opacity-100 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_10px_28px_-10px_rgba(15,23,42,0.18)] group-hover/marquee:opacity-50 hover:!opacity-100"
                    title={c.name}
                  >
                    {inner}
                    {fallback}
                  </CardTag>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
